<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { errAsync, okAsync } from 'neverthrow';
import { storeToRefs } from 'pinia';
import { InlineNotification } from '@sunlabde/m2m-library';
import CardCheckoutSummary from '../CardCheckoutSummary.vue';
import { CouponError } from '@/utilities/ErrorClasses';
import errorHandler from '@/utilities/ErrorHandler';
import { useCartStore } from '@/store/cart';
import { lookupCouponCode } from '@/api/coupon';
import { getPlanVariantById } from '@/api/planvariants';
import type { Cart } from '@/models/Cart';
import { useBillwerkStore } from '@/store/billwerk';
import { useMainStore } from '@/store/main';
import { getCouponPaymentPeriod } from '@/models/PaymentPeriod';
import { trackEvent } from '@/utilities/tracking';

const couponCode = ref('');
const waitingForValidation = ref(false);
const isCodeValid = ref(false);
const error = ref('');
const router = useRouter();
const cartStore = useCartStore();
const billwerkStore = useBillwerkStore();

const mainStore = useMainStore();
const { signupStyle } = storeToRefs(mainStore);

const onRedeemConfirm = () => {
	router.push({ name: 'initiatePayment' });
};

watch(couponCode, () => {
	error.value = '';
});

onMounted(() => {
	if (cartStore.cart?.M2MCouponCode) {
		couponCode.value = cartStore.cart?.M2MCouponCode;
		isCodeValid.value = true;
	}
});

const { t } = useI18n();

const onValidationSubmit = async () => {
	waitingForValidation.value = true;
	couponCode.value = couponCode.value.trim();

	const response = await lookupCouponCode(couponCode.value)
		.mapErr(() => new CouponError('notValid'))
		.andThen((couponResult) => {
			if (couponResult.redemptionState === 'GrantsLessPrivileges') {
				return errAsync(new CouponError('grantsLessPrivileges'));
			}

			if (couponResult.redemptionState === 'Disabled') {
				return errAsync(new CouponError('alreadyUsed'));
			}

			return okAsync(couponResult);
		})
		.andThen((couponResult) => getPlanVariantById(couponResult.planVariantID)
			.map((planVariantResult) => ({ couponResult, planVariantResult }))
			.mapErr(() => new CouponError('notValid')));

	waitingForValidation.value = false;

	if (response.isErr()) {
		errorHandler.report(response.error);
		isCodeValid.value = false;
		error.value = t('input.error.invalidCouponCode');
		return;
	}

	isCodeValid.value = true;

	const cart: Cart = { M2MCouponCode: couponCode.value, planVariant: response.value.planVariantResult };

	cartStore.setCartFromProduct(cart);
	mainStore.planCategory = cart.planVariant.CustomFields.category;
	billwerkStore.secretPaymentData = { bearer: 'None:None' };

	trackEvent('coupon_applied', { code_type: 'voucher' });
};
</script>

<template>
	<div class="mx-auto tablet:w-8/12 desktop:w-10/12">
		<h2 class="text-center font-serif font-semibold text-[24px] mb-8">
			<i18n-t
				v-if="signupStyle === 'thomann'"
				keypath="subscribe.step.coupon.title.thomann"
				scope="global"
			>
				<template #voucher>
					<span class="text-secondary-500">{{ $t('voucher') }}</span>
				</template>
			</i18n-t>
			<i18n-t
				v-else
				keypath="subscribe.step.coupon.title.default"
				scope="global"
			>
				<template #voucher_code>
					<span class="text-secondary-500">{{ $t('voucher_code') }}</span>
				</template>
			</i18n-t>
		</h2>
		<div class="w-full mb-8">
			<div class="flex flex-col desktop:flex-row">
				<div class="w-full mb-4 desktop:mb-0 desktop:mr-6">
					<AppInput
						v-model="couponCode"
						v-focus
						:disabled="isCodeValid"
						:label="$t('input.yourVoucherCode')"
						:error-message="error"
						icon="barcode-alt"
						@keypress.enter="onValidationSubmit"
					/>
				</div>
				<AppButton
					:is-loading="waitingForValidation"
					:is-success="isCodeValid"
					data-cy="button_coupon_redeem"
					class="whitespace-nowrap w-full mobile:w-auto h-fit"
					@click="onValidationSubmit"
				>
					{{ $t('button.redeemCouponCode') }}
				</AppButton>
			</div>
			<InlineNotification class="text-[14px] mt-8 desktop:max-w-xl mx-auto">
				{{ $t('subscribe.step.coupon.infoBox') }}
			</InlineNotification>
		</div>
		<Transition name="fade">
			<div v-if="isCodeValid">
				<div class="w-full mb-8 mt-16">
					<CardCheckoutSummary :payment-periods="[getCouponPaymentPeriod()]">
						<template #footer>
							<div class="flex justify-center">
								<AppButton
									data-cy="button_confirmSignup"
									size="md"
									class="whitespace-nowrap w-full mobile:w-auto"
									@click="onRedeemConfirm"
								>
									{{ $t('button.confirmOrder_coupon') }}
								</AppButton>
							</div>
						</template>
					</CardCheckoutSummary>
				</div>
			</div>
		</Transition>
	</div>
</template>
